import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../stores/hooks/hooks.ts";

import {
  RevHeader,
  RevLink,
  RevIconWrapper,
  RevStatusBadge,
  RevSelect,
  type SelectOption,
  RevEmptyState,
} from "@revmatics/catalyst";
import { switchBrand } from "../../../../stores/thunks/brandThunk.tsx";
import { ProfileMenu } from "./ProfileMenu.tsx";
import { getHeaderSelectAppsData } from "./headerData.tsx";

import Logo from "/images/logo_dark.svg";
import LogoMobile from "/images/logo_dark_small.svg";
import {
  faBuildingCircleArrowRight,
  faImage,
} from "@fortawesome/pro-solid-svg-icons";

const LAUNCHPAD_BASE_URL =
  import.meta.env.VITE_LAUNCHPAD_URL || "https://launchpad.revmatics.ai/";
const CHANGE_COMPANY_URL = `${LAUNCHPAD_BASE_URL.replace(/\/$/, "")}/change-company`;

type Props = {
  openSideBar: boolean;
  handleClick: () => void;
};

export const Header = ({ openSideBar, handleClick }: Props) => {
  const dispatch = useAppDispatch();
  const { showCroLinks } = useFlags();

  const [currentApp, setCurrentApp] = useState("audience_intelligence");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { companySettings } = useAppSelector((state) => state.company);
  const {
    brandList,
    currentBrand,
    isLoading: isLoadingBrandList,
  } = useAppSelector((state) => state.brand);
  const {
    organizationsList,
    currentOrganization: currentOrganizationId,
    isLoadingOrganizationsList,
  } = useAppSelector((state) => state.organizations);
  const navigate = useNavigate();

  const currentOrganization = useMemo(
    () =>
      organizationsList?.find((item) =>
        currentOrganizationId
          ? item.keycloak_organization_id === currentOrganizationId
          : item.is_default,
      ),
    [organizationsList, currentOrganizationId],
  );
  const hasMultipleOrganizations = (organizationsList?.length ?? 0) > 1;

  const isAdmin = useMemo(() => {
    if (!(organizationsList && companySettings)) return false;
    const currentOrg = organizationsList.find(
      (org) => org.id === companySettings?.id,
    );

    if (!currentOrg) return false;

    return currentOrg.groups.includes("INTERNAL_CUSTOMER_ONBOARDING");
  }, [organizationsList, companySettings]);

  const selectSortingBrands: SelectOption[] = useMemo(() => {
    if (!brandList) return [];
    return brandList
      .map((item) => ({
        value: item.id.toString(),
        label: item.name.trim(),
      }))
      .sort((a, b) => {
        const isAlpha = (s: string) => /^[a-z]/i.test(s.trim());
        return (
          Number(isAlpha(a.label)) - Number(isAlpha(b.label)) ||
          a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
        );
      });
  }, [brandList]);

  const currentBrandClassId = useMemo(
    () =>
      brandList?.find((b) => b.id.toString() === currentBrand)
        ?.cro_legacy_class_id ?? null,
    [brandList, currentBrand],
  );

  const filteredAppsSelectData = useMemo(
    () =>
      getHeaderSelectAppsData(currentBrandClassId).filter(
        (item) => item.value !== "cro_studio" || showCroLinks,
      ),
    [showCroLinks, currentBrandClassId],
  );

  // --- SHARED COMPONENTS ---

  const BrandSelector = (
    <RevSelect
      name="header-brand-select"
      onChange={(value) => {
        if (value && typeof value === "string") {
          dispatch(switchBrand({ brand_id: value }));
        }
      }}
      options={selectSortingBrands}
      dark={!mobileMenuOpen}
      value={currentBrand || undefined}
      searchable={selectSortingBrands.length > 20}
      searchDebounceMs={300}
      placeholder={
        isLoadingBrandList
          ? "Loading..."
          : brandList && brandList.length === 0
            ? "No active brand"
            : ""
      }
      className="w-full lg:w-[250px]"
      contentBefore={
        isLoadingOrganizationsList && !organizationsList ? (
          <div className="py-2 px-4 border-b border-monochrome-200 animate-pulse">
            <div className="h-3 w-14 bg-monochrome-200 rounded mb-2" />
            <div className="h-4 w-36 bg-monochrome-200 rounded mb-2" />
          </div>
        ) : currentOrganization ? (
          <div>
            <div className="py-2 px-4 border-b border-monochrome-200">
              <p className="typography-overline text-monochrome-400">Company</p>
              <p className="typography-body-sm text-monochrome-900">
                {currentOrganization.name}
              </p>
              {hasMultipleOrganizations ? (
                <RevLink
                  pathname={CHANGE_COMPANY_URL}
                  displayAs="button"
                  buttonVariant="secondary"
                  buttonSize="small"
                  icon={faBuildingCircleArrowRight}
                  text="Change Company"
                  className="mt-2 w-full"
                />
              ) : null}
            </div>
            <div
              className={`${selectSortingBrands.length > 20 ? "pt-2 pb-1" : "py-2"} px-4`}
            >
              <p className="typography-overline text-monochrome-400">Brand</p>
              {(!brandList || brandList.length === 0) && (
                <RevEmptyState
                  title=""
                  icon={false}
                  text="You don't have any brands yet."
                  btnText="Create Brand"
                  btnClassName="w-full"
                  btnSize="extraSmall"
                  handleClick={() => navigate("/company-settings/brand/create")}
                />
              )}
            </div>
          </div>
        ) : null
      }
    />
  );

  const AppSelector =
    isLoadingBrandList ||
    (brandList && brandList.length === 0) ||
    filteredAppsSelectData.length === 0 ? undefined : (
      <RevSelect
        name="header-app-select"
        onChange={(value) => {
          if (value && typeof value === "string") {
            setCurrentApp(value);
          }
        }}
        options={filteredAppsSelectData}
        dark={!mobileMenuOpen}
        value={currentApp}
        disabled={filteredAppsSelectData.length === 1}
        className="lg:w-[225px]"
        listWidth="!w-[216px] min-[992px]:!w-[225px]"
        contentAfter={
          <div
            className={`flex w-full items-center px-3 py-2 border-monochrome-100 border-t mt-1`}
          >
            <div className={`flex gap-2 items-start py-0.5`}>
              <RevIconWrapper icon={faImage} size={"l"} type={"primary"} />
              <div className="flex-1 typography-body-sm">
                <div className="flex w-full flex-col gap-1">
                  <span className="text-monochrome-900">
                    Creative Intelligence
                  </span>
                  <p className="typography-body-xs text-monochrome-400">
                    AI-powered creative scoring & optimization
                  </p>
                  <RevStatusBadge
                    text="Coming soon"
                    color="info"
                    variant="small"
                  />
                </div>
              </div>
            </div>
          </div>
        }
      />
    );

  return (
    <RevHeader
      openSideBar={openSideBar}
      handleSidebarToggle={handleClick}
      mobileMenuOpen={mobileMenuOpen}
      setMobileMenuOpen={setMobileMenuOpen}
      // LOGOS
      logo={
        <Link to="/">
          <img src={Logo} className="h-8" alt="Revmatics" />
        </Link>
      }
      logoMobile={
        <Link to="/">
          <img src={LogoMobile} className="h-8" alt="Revmatics" />
        </Link>
      }
      // SLOTS
      profileMenu={<ProfileMenu />}
      brandSelector={BrandSelector}
      appSelector={AppSelector}
      // ADMIN LINK
      desktopExtras={
        isAdmin && (
          <Link
            to="/companies"
            className="typography-body-sm text-primary-200 hover:text-primary-300"
          >
            Admin
          </Link>
        )
      }
      mobileExtras={
        isAdmin && (
          <RevLink pathname="/companies" text="Admin" className="w-fit ml-2" />
        )
      }
    />
  );
};
